import config from "../Configurations";
import axios from "axios";
import firebase, { provider } from "../FirebaseConfig.js";
import { getWithExpiry } from "./common";
import history from "../history";
import { buildLoginRedirectPath } from "./routeAccess";
import { withoutBlankPivotValues } from "./pivotFilter";
import { SHARE_HASH_HEADER, currentShareHash } from "./shareContext";

let sessionExpiryHandled = false;

/**
 * Central handler for backend auth rejection (HTTP 401 / "Missing Authorization
 * Header") and locally-expired tokens. Flags the login banner, clears session
 * state, signs out of Firebase, then redirects to /login. Guarded so several
 * concurrently-failing requests trigger a single redirect.
 */
const handleSessionExpired = () => {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;
  try {
    sessionStorage.setItem("loginFailed", true);
  } catch (_) {}
  window.localStorage.removeItem("jwt_token");
  window.localStorage.removeItem("selectedDashboard");
  firebase
    .auth()
    .signOut()
    .catch((error) => console.error("Sign Out Error", error))
    .finally(() => {
      const here = `${window.location.pathname}${window.location.search}`;
      document.location.href =
        document.location.origin + buildLoginRedirectPath(here);
    });
};

export const apiRequest = (
  url,
  method,
  payload,
  getFullResult,
  signal,
  pre_auth_check = false
) => {
  return new Promise((resolve) => {
    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    // WARNING: Do not set Access-Control-Allow-Origin on the client; it is a response
    // header only and can break CORS preflight (browser may require it in Allow-Headers).

    // modified version of getWithExpiry
    const tokenStr = window.localStorage.getItem("jwt_token");
    let tokenExpired = false;
    let token;

    if (tokenStr) {
      const item = JSON.parse(tokenStr);
      const now = new Date();
      if (now.getTime() > item.expiry) {
        window.localStorage.removeItem("jwt_token");
        tokenExpired = true;
      } else {
        token = item.value;
      }
    }

    if (tokenExpired && !pre_auth_check) {
      handleSessionExpired();
      resolve(false);
      return;
    }

    if (token) {
      myHeaders.append("Authorization", "Bearer " + token);
    }

    // On a public share page the API serves the shared dashboard only with its hash.
    const shareHash = currentShareHash();
    if (shareHash) {
      myHeaders.append(SHARE_HASH_HEADER, shareHash);
    }

    var requestOptions = {
      method: method,
      headers: myHeaders,
      redirect: "follow",
    };

    if (payload) {
      requestOptions.body = JSON.stringify(withoutBlankPivotValues(payload));
    }

    if (signal) {
      requestOptions.signal = signal;
    }

    fetch(config.BASE_URL + url, requestOptions)
      .then((response) =>
        response
          .json()
          .catch(() => ({}))
          .then((result) => ({ status: response.status, result }))
      )
      .then(({ status, result }) => {
        // Backend rejected auth (missing/invalid/expired token). Outside the
        // pre-auth flow this means the session is gone — sign out + redirect.
        // Not on a public share page: its visitor has no session to end.
        if (status === 401 && !pre_auth_check && !shareHash) {
          handleSessionExpired();
          resolve(getFullResult ? result : false);
          return;
        }
        if (status >= 400) {
          // httpStatus lets callers tell an outage (5xx) from a refusal;
          // see util/apiAvailability.
          resolve(
            getFullResult
              ? {
                  ...(typeof result === "object" && result !== null ? result : {}),
                  error: result?.error || `HTTP ${status}`,
                  httpStatus: status,
                }
              : false
          );
          return;
        }
        if (!result.error || getFullResult) {
          resolve(result);
        } else {
          resolve(false);
        }
      })
      .catch((error) => {
        const aborted = error?.name === "AbortError";
        if (!aborted) console.log(`Error in API ${url}`, error);
        // Aborted requests are intentional; anything else here is a network
        // failure (connection dropped, LB error page without CORS headers).
        resolve({ cancel: true, aborted });
      });
  });
};

/**
 * Upload a file to an API endpoint using multipart/form-data.
 * Handles JWT auth the same way apiRequest does.
 * Returns the parsed JSON response, or null on failure.
 */
export const apiFileUpload = (url, file, onProgress) => {
  const tokenStr = window.localStorage.getItem("jwt_token");
  let token = null;
  if (tokenStr) {
    const item = JSON.parse(tokenStr);
    const now = new Date();
    if (now.getTime() <= item.expiry) {
      token = item.value;
    }
  }

  const formData = new FormData();
  formData.append("file", file, file.name);

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", config.BASE_URL + url);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (!onProgress || !event.lengthComputable || !event.total) return;
      onProgress(Math.min(90, Math.floor((100 * event.loaded) / event.total)));
    };
    xhr.upload.onload = () => {
      if (onProgress) onProgress(90);
    };
    xhr.onload = () => {
      if (xhr.status === 413) {
        resolve({
          status: "failed",
          error_code: "ABS_ANALYSIS_TOO_LARGE",
        });
        return;
      }
      try {
        resolve(JSON.parse(xhr.responseText || "null"));
      } catch (err) {
        console.error(`[apiFileUpload] ${url}`, err);
        resolve(null);
      }
    };
    xhr.onerror = () => {
      console.error(`[apiFileUpload] ${url} network error`);
      resolve(null);
    };
    xhr.send(formData);
  });
};

export const uploadFileToS3 = (presignedPostData, file, setProgress) => {
  return new Promise((resolve) => {
    // Check if dual-write mode is enabled (nosqldb_and_buckets_target = 2 or 3)
    const isDualWrite = presignedPostData.dual_write === true && 
                        presignedPostData.primary && 
                        presignedPostData.secondary;
    
    // Log dual-write status for debugging
    if (isDualWrite) {
      console.log("[DUAL-WRITE] Dual-write mode detected, will upload to both primary and secondary storage");
      console.log("[DUAL-WRITE] Primary:", presignedPostData.primary);
      console.log("[DUAL-WRITE] Secondary:", presignedPostData.secondary);
    } else {
      console.log("[SINGLE-WRITE] Single-provider mode, uploading to primary storage only");
    }
    
    // Upload to primary storage first
    const uploadToPrimary = () => {
      return new Promise((resolvePrimary) => {
        // create a form obj
        const formData = new FormData();

        // Use primary URL if dual-write, otherwise use the original format
        const primaryData = isDualWrite ? presignedPostData.primary : presignedPostData;
        // Handle both dict format and direct format
        const uploadUrl = primaryData?.url || presignedPostData.url;
        const uploadFields = primaryData?.fields || presignedPostData.fields;
        
        if (!uploadUrl || !uploadFields) {
          console.error("[DUAL-WRITE] Invalid primary presigned POST data:", primaryData);
          resolvePrimary(false);
          return;
        }

        // append the fields in formData
        Object.keys(uploadFields).forEach((key) => {
          formData.append(key, uploadFields[key]);
        });

        // append the file
        formData.append("file", file);

        // post the data on the storage url
        axios
          .post(uploadUrl, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              let percentCompleted = Math.floor(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              if (setProgress) {
                // In dual-write mode, primary upload is 50% of total progress
                setProgress(isDualWrite ? Math.floor(percentCompleted / 2) : percentCompleted);
              }
            },
          })
          .then(function(response) {
            resolvePrimary(true);
          })
          .catch(function(error) {
            resolvePrimary(false);
            logError("Uploading Files to Primary Storage", error);
          });
      });
    };

    // Upload to secondary storage (only in dual-write mode)
    const uploadToSecondary = () => {
      return new Promise((resolveSecondary) => {
        // create a form obj
        const formData = new FormData();

        const secondaryData = presignedPostData.secondary;
        // Handle both dict format and direct format
        const uploadUrl = secondaryData?.url;
        const uploadFields = secondaryData?.fields;
        
        if (!uploadUrl || !uploadFields) {
          console.error("[DUAL-WRITE] Invalid secondary presigned POST data:", secondaryData);
          resolveSecondary(false);
          return;
        }

        // append the fields in formData
        Object.keys(uploadFields).forEach((key) => {
          formData.append(key, uploadFields[key]);
        });

        // append the file
        formData.append("file", file);

        // post the data on the secondary storage url
        axios
          .post(uploadUrl, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              let percentCompleted = Math.floor(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              if (setProgress) {
                // In dual-write mode, secondary upload is 50-100% of total progress
                setProgress(50 + Math.floor(percentCompleted / 2));
              }
            },
          })
          .then(function(response) {
            resolveSecondary(true);
          })
          .catch(function(error) {
            // Secondary upload failure is not critical, log warning but don't fail
            console.warn("Secondary storage upload failed (non-critical):", error);
            logError("Uploading Files to Secondary Storage", error);
            resolveSecondary(false);
          });
      });
    };

    // Upload to primary first
    uploadToPrimary().then((primarySuccess) => {
      if (!primarySuccess) {
        resolve(false);
        return;
      }

      // If dual-write mode, also upload to secondary
      if (isDualWrite) {
        uploadToSecondary().then((secondarySuccess) => {
          if (!secondarySuccess) {
            // Secondary upload failed - this is a problem in dual-write mode
            console.error("[DUAL-WRITE] Secondary storage upload failed! File may not be available in secondary storage.");
            logError("Secondary Storage Upload Failed in Dual-Write Mode", new Error("Secondary upload failed"));
            // Still resolve as true since primary succeeded, but log the error
          } else {
            console.log("[DUAL-WRITE] Both primary and secondary uploads succeeded");
          }
          // Primary succeeded, secondary is best-effort
          if (setProgress) {
            setProgress(100);
          }
          resolve(true);
        });
      } else {
        // Single-provider mode, primary upload is complete
        if (setProgress) {
          setProgress(100);
        }
        resolve(true);
      }
    });
  });
};

export const authWithGmail = () => {
  return new Promise((resolve) => {
    firebase
      .auth()
      .signInWithPopup(provider)
      .then((result) => {
        // var token = result.credential.accessToken;
        var user = result.user;
        resolve(user);
      })
      .catch(function(error) {
        logError("Gmail Auth", error);
        resolve(false);
      });
  });
};

export const signout = (redirect = true) => {
  firebase
    .auth()
    .signOut()
    .then(
      () => {
        // The API JWT belongs to the account that signed out; sent with the next
        // account's users/token it is refused by the actor guard.
        window.localStorage.removeItem("jwt_token");
        window.localStorage.removeItem("selectedDashboard");
        history.push("/login");
        // if (redirect) document.location.href = document.location.origin + "/";
      },
      function(error) {
        console.error("Sign Out Error", error);
      }
    );
};

export const logoutAllUsers = () => {
  firebase
    .database()
    .ref(`/users`)
    .once("value", async (snapshot) => {
      let users = snapshot.val();
      for (let key in users) {
        await firebase
          .database()
          .ref(`/users/${key}/mandatoryLogout`)
          .set(true);
      }
    });
};

export const logError = (location, error, extra = "") => {};
